import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getCurrentUser, getUserRole } from '../api/endpoints/users';
import { useNotificationStore } from './useNotificationStore';
import { logger } from '@/lib/logger';

export const useUserStore = create(
  devtools(
    (set, get) => ({
      // Estado inicial
      userProfile: null,
      userRole: null,
      isLoading: false,
      error: null,
      isInitialized: false,

      // Acciones
      setUserProfile: (profile) => set({ userProfile: profile }),
      setUserRole: (role) => set({ userRole: role }),
      
      clearUserProfile: () => set({ 
        userProfile: null, 
        userRole: null,
        error: null, 
        isInitialized: false 
      }),

      setError: (error) => set({ error, isLoading: false }),

      // Acción asíncrona para obtener el perfil del usuario
      fetchUserProfile: async () => {
        set({ isLoading: true, error: null });
        
        try {
          // Verificar si el backend está offline antes de hacer la llamada
          const backendStatus = window.__backendStatus || { isBackendOnline: true };
          if (!backendStatus.isBackendOnline) {
            set({ 
              error: 'Backend offline - Servicios no disponibles', 
              isLoading: false,
              isInitialized: false 
            });
            return;
          }
          
          // Use unified apiClient via endpoint function
          const userProfile = await getCurrentUser();
          
          set({ 
            userProfile, 
            isLoading: false, 
            error: null,
            isInitialized: true 
          });
          
          return userProfile;
        } catch (error) {
          logger.error('[USER_STORE] Error obteniendo perfil de usuario:', error);
          
          // Verificar si es un error de autenticación
          if (error.message?.includes('Missing Refresh Token') || 
              error.message?.includes('login_required') ||
              error.message?.includes('unauthorized')) {
            
            // Para errores de autenticación, NO marcar como inicializado
            // para que el hook pueda reintentar cuando el token se renueve
            set({ 
              error: error.message || 'Error de autenticación', 
              isLoading: false,
              isInitialized: false 
            });
          } else if (error.message?.includes('No se pudo conectar') ||
                     error.message?.includes('Network Error') ||
                     error.message?.includes('ERR_NETWORK') ||
                     error.message?.includes('Failed to fetch')) {
            // Para errores de red, NO marcar como inicializado
            // para permitir reintentos cuando el backend esté disponible
            set({ 
              error: 'Error de red: No se pudo conectar con el servidor', 
              isLoading: false,
              isInitialized: false 
            });
          } else {
            // Para otros errores, mostrar notificación y marcar como inicializado
            const { setNotification } = useNotificationStore.getState();
            setNotification(error.message, 'error');
            
            set({ 
              error: error.message || 'Error obteniendo perfil de usuario', 
              isLoading: false,
              isInitialized: true 
            });
          }
          
          throw error;
        }
      },

      // Acción asíncrona para obtener el rol del usuario
      fetchUserRole: async () => {
        try {
          // Use unified apiClient via endpoint function
          const roleData = await getUserRole();
          
          set({ userRole: roleData });
          
          return roleData;
        } catch (error) {
          logger.error('Error obteniendo rol de usuario:', error);
          
          // Verificar si es un error de autenticación
          if (error.message?.includes('Missing Refresh Token') || 
              error.message?.includes('login_required') ||
              error.message?.includes('unauthorized')) {
            
            // Para errores de autenticación, no mostrar notificación
            // El useAuthSync se encargará de manejar el error
          } else {
            // Para otros errores, mostrar notificación
            const { setNotification } = useNotificationStore.getState();
            setNotification(error.message, 'error');
          }
          
          throw error;
        }
      },

      // Acción para refrescar el perfil
      refreshUserProfile: async () => {
        const { fetchUserProfile } = get();
        return await fetchUserProfile();
      },

      // Getters computados
      getUserRole: () => {
        const { userRole } = get();
        return userRole?.role_in_company || 'OWNER';
      },

      getUserCompany: () => {
        const { userProfile } = get();
        return userProfile?.personal_company || null;
      },

      isCompanyOwner: () => {
        const { userProfile } = get();
        return userProfile?.personal_company?.id != null;
      },

      // Acción para inicializar el store (llamada después del login)
      initializeUserProfile: async () => {
        const { fetchUserProfile } = get();
        try {
          await fetchUserProfile();
        } catch (error) {
          logger.error('Error inicializando perfil de usuario:', error);
        }
      }
    }),
    {
      name: 'user-store', // Nombre para las devtools
    }
  )
);
